<template>
  <v-text-field
    :class="compClass"
    :label="label"
    :placeholder="placeholder"
    :type="type"
    :disabled="disabled"
    :readonly="readonly"
    v-model.trim="elValue"
    v-mask="vMask"
    :hide-details="hideDetails"
    @blur="onBlur"
    @focus="onFocus"
    v-validate="validateRules"
    :data-vv-name="validateName"
    :error-messages="errorMessages"
    :data-vv-validate-on="validateOn"
    :autofocus="autofocus"
  >
    <el-tooltip slot="append" v-if="tooltip" :tooltip="tooltip" absolute />
    <template slot="prepend">
      <slot name="prepend"></slot>
    </template>
    <template slot="append-outer">
      <slot name="append-outer"></slot>
    </template>
  </v-text-field>
</template>

<script>
export default {
  name: 'ElTextField',
  props: {
    classP: String,
    label: String,
    placeholder: { type: String, default: ' ' },
    type: String,
    tooltip: String,
    counter: [Number, String],
    required: Boolean,
    need: Boolean,
    hideDetails: [Boolean, String],
    date: Boolean,
    time: Boolean,
    email: Boolean,
    value: [String, Number],
    disabled: Boolean,
    readonly: Boolean,
    validateKey: String,
    pastDates: Boolean,
    futureDates: Boolean,
    today: String,
    autofocus: { type: Boolean, default: false },
    idk: Boolean,
    monthDay: Boolean,
    minYear: Number,
  },
  data() {
    return {}
  },
  inject: {
    $validator: '$validator',
  },
  computed: {
    elValue: {
      get() {
        return this.value
      },
      set(elValue) {
        this.$emit('input', elValue)
      },
    },
    validateName() {
      return this.validateKey ?? this.label
    },
    validateMessages() {
      return {
        'field may not be greater than': this.$t(
          'Viršytas simbolių skaičius {maxLength}',
          { maxLength: this.maxLength }
        ),
        'field must be at least 14': this.$t(
          'Identifikavimo kodas (IDK) turi būti 14 skaičių'
        ),
        'field is required': this.$t('Laukas privalomas'),
        'must be a valid email': this.$t('Netinkamas el. pašto formatas'),
        'must be in the format yyyy-MM-dd': this.$t(
          'Netinkamas datos formatas'
        ),
        'must be between 0001-01-01': this.$t(
          'Data negali būti didesnė už {todayDateText}',
          { todayDateText: this.todayDateText }
        ),
        'and 9999-01-01': this.$t(
          'Data negali būti ankstesnė už {todayDateText}',
          { todayDateText: this.todayDateText }
        ),
        'must be in the format HH:mm': this.$t('Netinkamas laiko formatas'),
        'field must be 1 or more': this.$t('Netinkama mėnesio diena'),
        'field must be 31 or less': this.$t('Netinkama mėnesio diena'),
        'field must be 2020 or more': this.$t('Gali būti 2020 ir vėlesni'),
        'field must be 2022 or more': this.$t('Gali būti 2022 ir vėlesni'),
      }
    },
    todayDateText() {
      let today = new Date()
      const realToday = `${today.getFullYear()}-${this.paddingZero(
        today.getMonth() + 1
      )}-${this.paddingZero(today.getDate())}`
      return this.today === realToday ? this.$t('šios dienos datą') : this.today
    },
    validateRules() {
      let rule = ''
      if (!this.readonly) {
        rule = this.required ? 'required' : ''
        rule += this.email ? (rule ? '|' : '') + 'email' : ''
        rule += this.date ? (rule ? '|' : '') + 'date_format:yyyy-MM-dd' : ''
        rule +=
          this.date && this.futureDates
            ? (rule ? '|' : '') + 'date_between:' + this.today + ',9999-01-01,1'
            : ''
        rule +=
          this.date && this.pastDates
            ? (rule ? '|' : '') + 'date_between:0001-01-01,' + this.today + ',1'
            : ''
        rule += this.time ? (rule ? '|' : '') + 'date_format:HH:mm' : ''
        rule += this.counter ? (rule ? '|' : '') + `max:${this.maxLength}` : ''
        rule += this.idk ? (rule ? '|' : '') + `max:14` : ''
        rule += this.monthDay
          ? (rule ? '|' : '') + `min_value:1|max_value:31`
          : ''
        rule += this.minYear
          ? (rule ? '|' : '') + `min_value:${this.minYear}`
          : ''
      }
      return rule
    },
    validateOn() {
      return (this.required && !this.email && !this.idk && !this.minYear) ||
        this.counter
        ? 'input'
        : 'change'
    },
    errorMessages() {
      const messages = this.errors.collect(this.validateName)
      if (messages.length > 0) {
        const key = Object.keys(this.validateMessages).find(item =>
          messages[0].includes(item)
        )
        return this.validateMessages[key]
      } else {
        return ''
      }
    },
    maxLength() {
      return this.counter
    },
    requiredClass() {
      return (this.required || this.need) && !this.readonly
        ? 'required-input'
        : ''
    },
    compClass() {
      return `${this.classP} ${this.requiredClass} labelPatch`
    },
    vMask() {
      return this.idk ? '##############' : ''
    },
  },
  methods: {
    onBlur(e) {
      this.$emit('blur', e.target.value)
    },
    onFocus(e) {
      this.$emit('focus', e.target.value)
    },
    paddingZero(str) {
      return String(str).padStart(2, '0')
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/elementsTheme.scss';
</style>
